/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { CircleButton } from "./circle";
import { useTooltipProps } from "./tooltip";
import { useTranslation } from "react-i18next";
import { TbRefresh } from "react-icons/tb";
import { Loader } from "./loader";
import { useCurrentRef } from "@/hooks/use-current-ref";
import { mapChildren } from "@/lib/utils";

type CurrentDateProps = {
  currentDate?: Date;
  now?: () => Date;
};

export const useCurrentDateState = ({
  currentDate: currentDate_,
  now: now_,
}: CurrentDateProps) => {
  const nowRef = React.useRef(now_!);
  if (now_) {
    nowRef.current = now_;
  } else if (!nowRef.current) {
    nowRef.current = () => new Date();
  }
  const [currentDate, setCurrentDate] = React.useState(
    currentDate_ ?? nowRef.current(),
  );
  const refresh = React.useCallback(
    () => setCurrentDate(nowRef.current()),
    [setCurrentDate, nowRef],
  );
  return {
    currentDate,
    setCurrentDate,
    refresh: refresh as typeof refresh | undefined,
  };
};

export type CurrentDateState = ReturnType<typeof useCurrentDateState>;

export const CurrentDateContext = React.createContext<CurrentDateState>(
  undefined as unknown as CurrentDateState,
);

export function CurrentDateProvider({
  children,
  ...props
}: CurrentDateProps & {
  children?: React.ReactNode;
}) {
  return (
    <CurrentDateContext.Provider
      value={useCurrentDateState(props)}
      children={children}
    />
  );
}

export function useCurrentDate(): CurrentDateState {
  return (
    React.useContext(CurrentDateContext) ?? {
      currentDate: new Date(),
      refresh: undefined,
      setCurrentDate: () => {},
    }
  );
}

export function RefreshCurrentDate({
  onRefresh,
  every,
}: {
  onRefresh?: () => any;
  every?: number;
}) {
  const { refresh } = useCurrentDate();
  const tooltipProps = useTooltipProps();
  const { t } = useTranslation();
  const ref = React.useRef<HTMLButtonElement | null>(null);
  const onRefreshRef = useCurrentRef(onRefresh);
  const onClick = React.useCallback(
    (
      ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      options?: { shouldRefresh: boolean },
    ) => {
      if (!refresh) return;
      if (ev) {
        ev.stopPropagation();
        ev.preventDefault();
      }
      if (refresh) {
        refresh();
      }
      if ((options?.shouldRefresh ?? true) && onRefreshRef.current) {
        onRefreshRef.current();
      }
      if (!ref.current) return;
      ref.current.dataset.active = "true";
      mapChildren(
        ref.current.children,
        (el: HTMLElement) => (el.dataset.active = "true"),
      );
      setTimeout(() => {
        if (!ref.current) return;
        ref.current.dataset.active = "false";
        mapChildren(
          ref.current.children,
          (el: HTMLElement) => (el.dataset.active = "false"),
        );
      }, 100);
    },
    [refresh],
  );
  React.useEffect(() => {
    if (!every || !refresh) return;
    let i = 0;
    const it = setInterval(() => {
      onClick(undefined, { shouldRefresh: i % 5 === 4 });
      i += 1;
    }, every);
    return () => clearInterval(it);
  }, [onClick, every, refresh]);
  return (
    refresh && (
      <CircleButton
        ref={ref}
        size={"sm"}
        onClick={onClick}
        {...tooltipProps}
        data-tooltip-content={t("refresh")}
      >
        <Loader className="hidden data-[active='true']:block" />
        <TbRefresh className="data-[active='true']:hidden -scale-x-100" />
      </CircleButton>
    )
  );
}
